import { useEffect, useState } from "react";
import { offstreetClient } from "../../../../api/client";
import type { RouterOutputs } from "../../../../api/types";
import { currentResumePath, usePermitFormActions } from "../../PermitContextProvider";
import type { PaymentFormProps } from "./PaymentForm";
import type { PaymentError } from "./types";

// A TouchNet ticket expires 15 minutes after it is issued, and uPay strands the parker on its own error page
// when it receives an expired one, so an open TouchNet checkout swaps in a fresh session before then.
const TOUCHNET_SESSION_REFRESH_MS = 13 * 60_000;

type UsePaymentSessionProps = Pick<PaymentFormProps, "tenantId" | "locale" | "setDuplicatedPermit"> & {
	onError: (error: PaymentError) => void;
};

export const usePaymentSession = ({ tenantId, locale, setDuplicatedPermit, onError }: UsePaymentSessionProps) => {
	const { getPermitPostData, setError } = usePermitFormActions();
	const [paymentSession, setPaymentSession] =
		useState<RouterOutputs["paymentSession"]["createPaymentSessionPublic"]>();
	const [sessionRequest, setSessionRequest] = useState(0);
	const [isCreating, setIsCreating] = useState(true);
	const [isRefreshAfterPaymentError, setIsRefreshAfterPaymentError] = useState(false);
	const { mutate: createPaymentSessionPublic } =
		offstreetClient.paymentSession.createPaymentSessionPublic.useMutation();

	// biome-ignore lint/correctness/useExhaustiveDependencies: Only request a payment session on mount and on requestNewSession
	useEffect(() => {
		createPaymentSessionPublic(
			{
				...getPermitPostData(),
				tenantId,
				returnUrl: `${window.location.origin}/${locale}/payments/touchnet/callback?resume=${encodeURIComponent(currentResumePath())}`,
			},
			{
				onSuccess: (data) => {
					if (data.session) {
						setPaymentSession(data);
					} else if (data.permit.isPaymentPending) {
						onError({ type: "unconfirmed" });
					} else {
						setDuplicatedPermit({
							plate: data.permit.permit_vehicles_vehicle[0]?.vehicle.plate!,
							permitIntervals: data.permit.permitIntervals.map((interval) => ({
								startDateTimeISO: interval.startDateTime,
								endDateTimeISO: interval.endDateTime,
							})),
							publicIdentifier: data.permit.publicIdentifier,
						});
					}
				},
				onError: (error) => {
					if (!isRefreshAfterPaymentError) {
						setError(error);
					}
				},
				onSettled: () => setIsCreating(false),
			},
		);
	}, [sessionRequest, tenantId]);

	const requestNewSession = ({ afterPaymentError = false }: { afterPaymentError?: boolean } = {}) => {
		setIsRefreshAfterPaymentError(afterPaymentError);
		setIsCreating(true);
		setError(undefined);
		setPaymentSession(undefined);
		setSessionRequest((count) => count + 1);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: requestNewSession only calls state setters
	useEffect(() => {
		if (!paymentSession?.session?.client?.touchnet) {
			return;
		}
		const timer = setTimeout(() => requestNewSession(), TOUCHNET_SESSION_REFRESH_MS);
		return () => clearTimeout(timer);
	}, [paymentSession]);

	return { paymentSession, isCreating, requestNewSession };
};
